import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getSignedUrl } from '@/api/storage';
import { Button } from './ui/Button';
import { isNative, useShowBottomNav } from '@/lib/platform';
import { pageShell } from '@/lib/styles';

export function Header() {
  const minimal = useShowBottomNav();
  if (isNative || minimal) return <NativeHeader />;
  return <WebHeader />;
}

function NativeHeader() {
  return (
    <header className="sticky top-0 z-[1001] bg-white pt-[env(safe-area-inset-top)] shadow-lg shadow-teal-950/10">
      <div className="border-b border-line bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-4 sm:px-6">
          <Link
            to="/"
            className="shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <span className="font-display text-xl font-semibold tracking-tight text-ink drop-shadow-sm">
              imóveis<span className="text-brand">já</span>
            </span>
          </Link>
        </div>
      </div>
    </header>
  );
}

function WebHeader() {
  const { user, profile, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (profile?.avatar_path) {
      getSignedUrl(profile.avatar_path).then(setAvatarUrl).catch(() => setAvatarUrl(null));
    } else {
      setAvatarUrl(null);
    }
  }, [profile?.avatar_path]);

  const displayName = profile?.display_name ?? (user?.user_metadata?.full_name as string | undefined) ?? '';
  const initials = displayName
    ? displayName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : (user?.email?.slice(0, 2).toUpperCase() ?? '?');
  const isPremium = !!(profile?.is_premium && profile?.premium_until && new Date(profile.premium_until) > new Date());

  return (
    <header className="sticky top-0 z-[1001] border-b border-white/60 bg-white/80 shadow-lg shadow-teal-950/5 backdrop-blur-2xl">
      <div className={`${pageShell} flex h-16 items-center justify-between gap-4`}>
        <Link
          to="/"
          className="group shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <span className="font-display text-xl font-semibold tracking-tight text-ink transition-transform duration-200 group-hover:-translate-y-0.5">
            imóveis<span className="text-brand">já</span>
          </span>
        </Link>

        <nav className="flex items-center gap-2">
          <Link
            to="/mapa"
            className={[
              'flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-sm font-medium transition-colors',
              location.pathname === '/mapa'
                ? 'bg-brand/10 text-brand shadow-sm'
                : 'text-muted hover:bg-white hover:text-ink hover:shadow-input',
            ].join(' ')}
            aria-label="Mapa de imóveis"
          >
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <span className="hidden sm:inline">Mapa</span>
          </Link>

          {loading ? (
            <div
              className="h-9 w-24 rounded-full bg-faint animate-shimmer"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(208,208,208,0.55) 0%, rgba(240,240,240,0.9) 50%, rgba(208,208,208,0.55) 100%)',
                backgroundSize: '200% 100%',
              }}
              aria-hidden="true"
            />
          ) : user ? (
            <>
              <Button variant="primary" size="sm" onClick={() => navigate('/anunciar', { replace: true })}>
                Anunciar
              </Button>

              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setOpen(v => !v)}
                  className={[
                    'flex items-center rounded-full border border-brand/15 bg-white/80 transition-all duration-200',
                    'hover:-translate-y-0.5 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                    open ? 'shadow-card' : '',
                    avatarUrl ? 'gap-2.5 px-3 py-2' : 'p-2.5',
                  ].join(' ')}
                  aria-label="Menu do usuário"
                  aria-expanded={open}
                  aria-haspopup="menu"
                >
                  <svg className="h-4 w-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  {avatarUrl && (
                    <div className="relative h-7 w-7 shrink-0">
                      <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-brand text-xs font-bold text-white">
                        <img src={avatarUrl} alt={displayName || 'Avatar'} className="h-full w-full object-cover" />
                      </div>
                      {isPremium && (
                        <span className="pointer-events-none absolute -right-0.5 -bottom-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-white bg-gradient-to-br from-yellow-400 to-amber-500 shadow-sm">
                          <svg viewBox="0 0 20 20" fill="currentColor" className="h-2 w-2 text-white" aria-hidden="true">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </span>
                      )}
                    </div>
                  )}
                </button>

                {open && (
                  <div
                    role="menu"
                    className="absolute right-0 z-[1001] mt-2 w-60 overflow-hidden rounded-2xl border border-line bg-white opacity-100 shadow-dropdown animate-profile-menu-pop"
                  >
                    <button
                      onClick={() => { navigate('/perfil', { replace: true }); setOpen(false); }}
                      className="flex w-full items-center gap-3 border-b border-line px-4 py-3 text-left transition-colors hover:bg-faint focus-visible:bg-faint focus-visible:outline-none"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand text-sm font-bold text-white">
                        {avatarUrl
                          ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" aria-hidden="true" />
                          : <span>{initials}</span>}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-ink">{displayName || 'Minha conta'}</p>
                        {isPremium ? (
                          <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 px-1.5 py-0.5 text-xs font-semibold text-white">
                            <svg viewBox="0 0 20 20" fill="currentColor" className="h-2.5 w-2.5 shrink-0" aria-hidden="true">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Premium
                          </span>
                        ) : (
                          <p className="truncate text-xs text-muted">{user.email}</p>
                        )}
                      </div>
                    </button>

                    <div className="border-t border-line py-1">
                      <MenuItem onClick={() => { navigate('/meus-anuncios', { replace: true }); setOpen(false); }} icon={<ListIcon />} label="Meus anúncios" />
                      {!isPremium && (
                        <MenuItem
                          onClick={() => { navigate('/premium', { replace: true }); setOpen(false); }}
                          icon={<StarIcon />}
                          label="Conheça o Premium"
                          highlight
                        />
                      )}
                      {isPremium && (
                        <MenuItem onClick={() => { navigate('/painel', { replace: true }); setOpen(false); }} icon={<ChartIcon />} label="Painel Premium" />
                      )}
                      {profile?.is_admin && (
                        <MenuItem onClick={() => { navigate('/admin', { replace: true }); setOpen(false); }} icon={<ShieldIcon />} label="Painel Admin" />
                      )}
                      <MenuItem onClick={() => { navigate('/favoritos', { replace: true }); setOpen(false); }} icon={<HeartIcon />} label="Favoritos" />
                    </div>
                    <div className="border-t border-line py-1">
                      <MenuItem
                        onClick={() => {
                          void signOut().then(() => navigate('/', { replace: true }));
                          setOpen(false);
                        }}
                        icon={<LogoutIcon />}
                        label="Sair"
                        muted
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate('/login', { replace: true })}>Entrar</Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/anunciar', { replace: true })}>Anunciar</Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

interface MenuItemProps {
  onClick: () => void;
  icon: ReactNode;
  label: string;
  highlight?: boolean;
  muted?: boolean;
}

function MenuItem({ onClick, icon, label, highlight, muted }: MenuItemProps) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className={[
        'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm',
        'transition-colors focus-visible:bg-faint focus-visible:outline-none',
        highlight ? 'font-semibold text-brand hover:bg-brand/5'
          : muted ? 'text-muted hover:bg-faint'
            : 'text-ink hover:bg-faint',
      ].join(' ')}
    >
      <span className={`h-4 w-4 shrink-0 ${highlight ? 'text-brand' : 'text-muted'}`}>{icon}</span>
      {label}
    </button>
  );
}

function ChartIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>;
}
function ListIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>;
}
function HeartIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" /></svg>;
}
function LogoutIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>;
}
function ShieldIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
}
function StarIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.52 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.364 1.118l1.52 4.674c.3.921-.755 1.688-1.54 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.784.57-1.838-.197-1.539-1.118l1.52-4.674a1 1 0 00-.364-1.118L2.49 10.1c-.783-.57-.38-1.81.588-1.81h4.915a1 1 0 00.951-.69l1.52-4.674z" /></svg>;
}
